import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

import { useSocket } from 'utils/hooks/useSocket';
import { useTournament } from 'context/TournamentContext';
import { getTournamentRanking } from 'api/tournamentService';
import { formatCurrency } from 'utils';
import type { RankingEntry } from 'types/tournament';
import { CASH_TICKER } from 'utils/cash';

import {
  Wrap,
  Head,
  SectionTitle,
  List,
  Row,
  Rank,
  Name,
  Pct,
  Amount,
  Empty,
} from './styled';

type RankRow = {
  username: string;
  pct: number;
  rank: number;
  isMe: boolean;
  totalInUSDT: number;
};

const RankingCompact: React.FC = () => {
  const { tournamentRanking } = useSocket() as { tournamentRanking: RankingEntry[] };
  const { tournamentDetails } = useTournament();
  const [fetched, setFetched] = useState<RankingEntry[]>([]);

  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData ?? {};
  const tournamentId = rawData?.id;
  const initialMoney = Number(attributes?.amount_start) || 1;

  const username = localStorage.getItem('username') ?? Cookies.get('username') ?? '';

  useEffect(() => {
    const liveLen = Array.isArray(tournamentRanking) ? tournamentRanking.length : 0;
    if (liveLen === 0 && tournamentId) {
      getTournamentRanking(tournamentId)
        .then((data) => setFetched(Array.isArray(data) ? data : []))
        .catch(() => {});
    } else {
      setFetched([]);
    }
  }, [tournamentRanking, tournamentId]);

  const liveRanking = Array.isArray(tournamentRanking) && tournamentRanking.length > 0
    ? tournamentRanking
    : fetched;

  const ranked: RankRow[] = useMemo(() => {
    return liveRanking
      .map((entry) => {
        const total = Number(entry.totalInUSDT);
        const pct = Number.isFinite(total)
          ? ((total - initialMoney) / initialMoney) * 100
          : 0;
        return { username: entry.username, totalInUSDT: total, pct };
      })
      .sort((a, b) => b.totalInUSDT - a.totalInUSDT)
      .map((row, idx) => ({
        username: row.username,
        pct: row.pct,
        rank: idx + 1,
        isMe: row.username === username,
        totalInUSDT: row.totalInUSDT,
      }));
  }, [liveRanking, initialMoney, username]);

  return (
    <Wrap>
      <Head>
        <SectionTitle>Tournament Ranking</SectionTitle>
      </Head>
      <List>
        {ranked.length === 0 && <Empty>No entries yet</Empty>}
        {ranked.map((row) => {
          const cls = row.pct > 0 ? 'pos' : row.pct < 0 ? 'neg' : '';
          return (
            <Row key={row.username} $me={row.isMe}>
              <Rank>{row.rank}</Rank>
              <Name $me={row.isMe}>{row.username}</Name>
              <Pct $pos={cls === 'pos'} $neg={cls === 'neg'}>
                {row.pct > 0 ? '+' : ''}
                {row.pct.toFixed(2)}%
              </Pct>
              <Amount $pos={cls === 'pos'} $neg={cls === 'neg'}>
                {formatCurrency(row.totalInUSDT)} {CASH_TICKER}
              </Amount>
            </Row>
          );
        })}
      </List>
    </Wrap>
  );
};

export default RankingCompact;
